/**
 * WAF Access Control (Owl/Mlytics) — per-domain allow/deny lists keyed by
 * source IP/CIDR or geo code. Service-scoped and domain-anchored (`?domain=<fqdn>`);
 * the provider resolves the customer (tier-2 impersonation) from the service's zone.
 *
 * Backed by `modules/service/waf/controller_access_control.go` — four flat lists
 * addressed by `{kind}/{list}` (`ip|geo` × `white|black`):
 *   - `GET    /services/:service_id/waf/access-control/{kind}/{list}` → paginated entries
 *   - `POST   /services/:service_id/waf/access-control/{kind}/{list}` → add `{ values: [] }`
 *   - `DELETE /services/:service_id/waf/access-control/{kind}/{list}` → remove `{ ids: [] }`
 *
 * Entries are add/delete-only — there is no enable/disable or action on an entry
 * (unlike CRS rules). A single-row delete is just a one-element `ids`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap, type PageInfo } from './_shared';

/** Entry match dimension. */
export type WafAcKind = 'ip' | 'geo';
/** Allowlist (white) vs blocklist (black). */
export type WafAcList = 'white' | 'black';

/** One access-control entry (mirrors BE `providers.WAFAccessControlEntry`). */
export interface WafAccessControlEntry {
  id: number;
  kind: WafAcKind;
  list: WafAcList;
  /** IP/CIDR (kind=ip) or hierarchical geo code, e.g. "AS-VN" (kind=geo). */
  value: string;
}

/** List response (mirrors BE `providers.WAFAccessControlResponse`). */
export interface WafAccessControlResult {
  entries: WafAccessControlEntry[];
  pagination: PageInfo;
}

/** List a domain's access-control entries for one kind+list (server-paginated). */
export function useWafAccessControl(
  serviceId: string | undefined,
  domain: string | undefined,
  kind: WafAcKind,
  list: WafAcList,
  page: { page: number; pageSize: number },
) {
  const params = {
    domain,
    page: page.page,
    page_size: page.pageSize,
  };
  return useQuery<WafAccessControlResult>({
    queryKey: wafKeys.accessControl(serviceId ?? '', domain ?? '', kind, list, {
      page: page.page,
      page_size: page.pageSize,
    }),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<WafAccessControlResult>(
        await serviceApiV3.get(`/services/${serviceId}/waf/access-control/${kind}/${list}`, { params }),
      ),
  });
}

/** Add entries to a kind+list (`POST` body `{ values }`). */
export function useAddWafAccessControl(
  serviceId: string | undefined,
  options?: UseMutationOptions<
    unknown,
    Error,
    { domain: string; kind: WafAcKind; list: WafAcList; values: string[] }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      domain,
      kind,
      list,
      values,
    }: {
      domain: string;
      kind: WafAcKind;
      list: WafAcList;
      values: string[];
    }) =>
      unwrap<unknown>(
        await serviceApiV3.post(
          `/services/${serviceId}/waf/access-control/${kind}/${list}`,
          { values },
          { params: { domain } },
        ),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'access-control'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Remove entries from a kind+list by id (`DELETE` body `{ ids }`). */
export function useDeleteWafAccessControl(
  serviceId: string | undefined,
  options?: UseMutationOptions<
    unknown,
    Error,
    { domain: string; kind: WafAcKind; list: WafAcList; ids: number[] }
  >,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      domain,
      kind,
      list,
      ids,
    }: {
      domain: string;
      kind: WafAcKind;
      list: WafAcList;
      ids: number[];
    }) =>
      unwrap<unknown>(
        await serviceApiV3.delete(`/services/${serviceId}/waf/access-control/${kind}/${list}`, {
          data: { ids },
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'access-control'] });
      options?.onSuccess?.(...args);
    },
  });
}
